import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["avatar", "children", "size", "isSelected", "icon", "iconPosition", "onClick", "onDismiss", "colorScheme", "dismissButtonRef", "dismissButtonAltText", "backgroundColor"];
import React, { forwardRef } from 'react';
import { describe, PropTypes } from '@amzn/stencil-react-desc';
import { withTestAttributes } from "../utils";
import ChipBase from "./ChipBase";
import { ChipClickableContainer, ChipStaticContainer } from "./helperComponents";
import { ChipSize, ChipIconPosition, ChipColorScheme, ChipBackgroundColor } from "./interfaces";
import { jsx as ___EmotionJSX } from "@emotion/core";
var ChipWithoutDocs = /*#__PURE__*/forwardRef(function (props, ref) {
  var avatar = props.avatar,
      children = props.children,
      _props$size = props.size,
      size = _props$size === void 0 ? ChipSize.Default : _props$size,
      isSelected = props.isSelected,
      icon = props.icon,
      _props$iconPosition = props.iconPosition,
      iconPosition = _props$iconPosition === void 0 ? ChipIconPosition.Leading : _props$iconPosition,
      onClick = props.onClick,
      onDismiss = props.onDismiss,
      _props$colorScheme = props.colorScheme,
      colorScheme = _props$colorScheme === void 0 ? ChipColorScheme.Default : _props$colorScheme,
      dismissButtonRef = props.dismissButtonRef,
      _props$dismissButtonA = props.dismissButtonAltText,
      dismissButtonAltText = _props$dismissButtonA === void 0 ? 'Remove' : _props$dismissButtonA,
      _props$backgroundColo = props.backgroundColor,
      backgroundColor = _props$backgroundColo === void 0 ? ChipBackgroundColor.Default : _props$backgroundColo,
      htmlProps = _objectWithoutProperties(props, _excluded);

  var chipBaseProps = {
    avatar: avatar,
    children: children,
    size: size,
    icon: icon,
    iconPosition: iconPosition,
    onClick: onClick,
    onDismiss: onDismiss,
    colorScheme: colorScheme,
    dismissButtonRef: dismissButtonRef,
    dismissButtonAltText: dismissButtonAltText,
    backgroundColor: backgroundColor
  };

  var baseContent = ___EmotionJSX(ChipBase, chipBaseProps);

  var baseContainerProps = {
    colorScheme: colorScheme
  };
  return onClick && !avatar ? ___EmotionJSX(ChipClickableContainer, _extends({
    ref: ref,
    isSelected: isSelected,
    "aria-pressed": isSelected,
    onClick: onClick,
    size: size
  }, baseContainerProps, htmlProps), baseContent) : ___EmotionJSX(ChipStaticContainer, _extends({
    ref: ref,
    backgroundColor: backgroundColor,
    size: size
  }, baseContainerProps, htmlProps), baseContent);
});
var ChipWithDocs = describe(ChipWithoutDocs).description('Compact element that represent an input, attribute, or action');
ChipWithDocs.defaultProps = {
  size: ChipSize.Default,
  iconPosition: ChipIconPosition.Leading,
  colorScheme: ChipColorScheme.Default,
  backgroundColor: ChipBackgroundColor.Default,
  dismissButtonAltText: 'Remove'
};
ChipWithDocs.propTypes = {
  avatar: PropTypes.shape({
    username: PropTypes.string.isRequired,
    fullName: PropTypes.string,
    isUsernameClickable: PropTypes.bool,
    newTabHiddenText: PropTypes.string
  }).description('Show avatar as chip content.'),
  children: PropTypes.node.description('Text to display in the chip.'),
  size: PropTypes.oneOf(Object.values(ChipSize)).description('Specifies what size of the chip should be used.'),
  isSelected: PropTypes.bool.description("\n  'Determines whether the chip is currently selected by showing a visual indication.'\n  "),
  icon: PropTypes.node.description('Adds an icon on the leading or trailing side of the chip.'),
  iconPosition: PropTypes.oneOf(Object.values(ChipIconPosition)).description('Determines on which side the icon would be placed.'),
  onClick: PropTypes.func.description('Makes the chip to be clickable. This function will then be called when the user clicks the chip.').format('() => void'),
  onDismiss: PropTypes.func.description('Makes the chip to be dismissible. This function will then be  called when the user clicks the X button (dismiss button) in the chip. The argument allows to distinguish whether the action was triggered by mouse or keyboard').format("(triggeredBy: 'keyboard' | 'mouse' ) => void"),
  colorScheme: PropTypes.oneOf(Object.values(ChipColorScheme)).description('Selects a predefined color scheme to use for the component.'),
  dismissButtonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]).description('Reference to attach to the dismiss button.'),
  dismissButtonAltText: PropTypes.string.description('Alt text used for the dismiss button in the chip.'),
  backgroundColor: PropTypes.string.description('Sets the background color for the static chip. Does not affect the text color, so only colors with enough contrast should be used.')
};
var Chip = withTestAttributes({
  component: 'StencilChip'
})(ChipWithDocs);
Chip.displayName = 'Chip';
export default Chip;